import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { monthsFor, toIcrView, type IcrViewMonth, type IcrRequestParams, type IcrViewRow } from './icr-model';

/**
 * Data hooks for /product-insights/icr.
 *
 * - `useIcrSummary` is the page's one read (`groupBy=university`). The last
 *   successful answer stays on screen while a newer key loads (`refreshing`);
 *   `loading` is only true when there is nothing to show yet. Loading is
 *   derived from request keys, never set inside the effect.
 * - `useIcrMonths` is click-bounded: it fetches a row's month-by-month figures
 *   only once that row is expanded, narrowed to the row's company when it has
 *   one, and caches per row + filter key for the life of the page.
 */

export type IcrViewData = ReturnType<typeof toIcrView>;

interface SummaryResult {
  key: string;
  data: IcrViewData | null;
  error: unknown | null;
}

export function useIcrSummary(
  params: IcrRequestParams | null,
  reloadToken: number,
): { data: IcrViewData | null; loading: boolean; error: unknown | null; refreshing: boolean } {
  const paramsKey = params === null ? null : JSON.stringify(params);
  const requestKey = paramsKey === null ? null : `${paramsKey}#${reloadToken}`;
  const [lastData, setLastData] = useState<IcrViewData | null>(null);
  const [result, setResult] = useState<SummaryResult | null>(null);

  useEffect(() => {
    if (paramsKey === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${paramsKey}#${reloadToken}`;
    const request = JSON.parse(paramsKey) as IcrRequestParams;
    api
      .productInsightsIcr(request, signal)
      .then((raw: unknown) => {
        if (signal.aborted) return;
        const data = toIcrView(raw);
        setLastData(data);
        setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setResult({ key, data: null, error: err });
      });
    return () => controller.abort();
  }, [paramsKey, reloadToken]);

  const current = result !== null && result.key === requestKey ? result : null;
  const settled = current !== null;
  return {
    data: lastData,
    loading: lastData === null && !settled,
    error: current?.error ?? null,
    refreshing: lastData !== null && !settled,
  };
}

export type IcrMonthsState =
  | { kind: 'loading' }
  | { kind: 'error'; error: unknown }
  | { kind: 'ready'; months: IcrViewMonth[] | null };

/**
 * Month rows for every expanded row. `base` is the summary's filter set; each
 * expanded row adds `groupBy=month` and its company id.
 */
export function useIcrMonths(
  base: IcrRequestParams | null,
  rows: readonly IcrViewRow[],
  expanded: ReadonlySet<string>,
  reloadToken: number,
): Map<string, IcrMonthsState> {
  const baseKey = base === null ? null : JSON.stringify(base);
  const [results, setResults] = useState<Map<string, IcrMonthsState>>(() => new Map());
  // Mirrors the SETTLED keys in `results`, read inside the effect so a cached
  // row is never asked twice. An in-flight row has no entry: absent = loading,
  // so an aborted request simply leaves nothing behind and is asked again.
  const settled = useRef<Set<string>>(new Set());

  const wanted = rows.filter((row) => expanded.has(row.key));
  const wantedKey = wanted.map((row) => row.key).join('|');

  useEffect(() => {
    if (baseKey === null || wantedKey === '') return;
    const controller = new AbortController();
    const { signal } = controller;
    const request = JSON.parse(baseKey) as IcrRequestParams;
    const keys = new Set(wantedKey.split('|'));
    for (const row of rows) {
      if (!keys.has(row.key)) continue;
      const cacheKey = `${baseKey}#${reloadToken}#${row.key}`;
      if (settled.current.has(cacheKey)) continue;
      const narrowed = row.universityCompanyId !== null;
      const params: IcrRequestParams = {
        ...request,
        groupBy: 'month',
        ...(narrowed && row.universityCompanyId !== null ? { companyId: row.universityCompanyId } : {}),
      };
      api
        .productInsightsIcr(params, signal)
        .then((raw: unknown) => {
          if (signal.aborted) return;
          const view = toIcrView(raw);
          settled.current.add(cacheKey);
          setResults((prev) =>
            new Map(prev).set(cacheKey, { kind: 'ready', months: monthsFor(row, view, narrowed) }),
          );
        })
        .catch((error: unknown) => {
          if (signal.aborted) return;
          settled.current.add(cacheKey);
          setResults((prev) => new Map(prev).set(cacheKey, { kind: 'error', error }));
        });
    }
    return () => controller.abort();
  }, [baseKey, wantedKey, reloadToken, rows]);

  const byRow = new Map<string, IcrMonthsState>();
  if (baseKey === null) return byRow;
  for (const row of wanted) {
    const state = results.get(`${baseKey}#${reloadToken}#${row.key}`);
    byRow.set(row.key, state ?? { kind: 'loading' });
  }
  return byRow;
}
